import { useScrollReveal } from '../../../hooks/useScrollReveal';

const stats = [
  { value: "500+", label: "Businesses Automated" },
  { value: "98%", label: "Client Retention Rate" },
  { value: "24/7", label: "Systems Always Running" },
  { value: "3×", label: "Avg. Revenue Growth" },
];

const AboutSection = () => {
  const { ref: tagRef, visible: tagVisible } = useScrollReveal();
  const { ref: headRef, visible: headVisible } = useScrollReveal();
  const { ref: bodyRef, visible: bodyVisible } = useScrollReveal();
  const { ref: imgRef, visible: imgVisible } = useScrollReveal();
  const { ref: statsRef, visible: statsVisible } = useScrollReveal();

  return (
    <section className="bg-[#1d1d1f] text-white py-32 lg:py-44 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">

        {/* Top label */}
        <div
          ref={tagRef}
          className={`reveal-el ${tagVisible ? 'is-visible' : ''} mb-10`}
        >
          <p className="text-sapphire text-xs font-bold uppercase tracking-widest">Who We Are</p>
        </div>

        {/* Two-column: headline + body */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mb-20">
          <div
            ref={headRef}
            className={`reveal-el ${headVisible ? 'is-visible' : ''}`}
          >
            <h2 className="text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.04] tracking-tight text-white">
              Built to Make<br />
              <span className="text-white/40">Your Business</span><br />
              Unstoppable.
            </h2>
          </div>

          <div
            ref={bodyRef}
            className={`reveal-el ${bodyVisible ? 'is-visible' : ''} flex flex-col justify-center gap-6`}
          >
            <p className="text-white/70 text-lg leading-relaxed">
              Blue Digital AI was founded on one belief: that small and mid-size businesses deserve the same automation power as the Fortune 500. Too many great companies lose revenue every day simply because their intake process can't keep up.
            </p>
            <p className="text-white/70 text-lg leading-relaxed">
              We design and deploy AI-powered systems — voice agents, smart follow-up sequences, automated booking pipelines — that operate without gaps, without burnout, and without human error. Your team focuses on delivering great work. Our systems make sure nothing falls through the cracks.
            </p>
            <p className="text-white/50 text-sm leading-relaxed border-l-2 border-sapphire pl-5">
              "We don't sell software. We build the infrastructure that lets your business grow — quietly, reliably, relentlessly."
            </p>
          </div>
        </div>

        {/* Full-bleed cinematic image */}
        <div
          ref={imgRef}
          className={`reveal-scale ${imgVisible ? 'is-visible' : ''} relative w-full h-[360px] lg:h-[520px] rounded-3xl overflow-hidden mb-20`}
        >
          <img
            src="https://public.readdy.ai/ai/img_res/bdc6e71cc6d1368a9b75247eaa9dea51.jpg"
            alt="Blue Digital AI workspace"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1d1d1f] via-[#1d1d1f]/20 to-transparent"></div>

          {/* Floating badge */}
          <div className="absolute bottom-8 left-8 right-8 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl px-6 py-4">
              <p className="text-white font-black text-2xl leading-none">AI-First.</p>
              <p className="text-white/60 text-sm mt-1">Every system we build is intelligence-native.</p>
            </div>
            <div className="backdrop-blur-md bg-white/10 border border-white/20 rounded-2xl px-6 py-4">
              <p className="text-white font-black text-2xl leading-none">Human-Driven.</p>
              <p className="text-white/60 text-sm mt-1">Strategy designed by real experts, for real growth.</p>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div
          ref={statsRef}
          className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-3xl overflow-hidden"
        >
          {stats.map((stat, i) => (
            <div
              key={stat.label}
              className={`reveal-el stagger-${i + 1} ${statsVisible ? 'is-visible' : ''} bg-[#1d1d1f] px-8 py-10 flex flex-col gap-2 group hover:bg-white/5 transition-colors duration-300`}
            >
              <span className="text-5xl lg:text-6xl font-black text-white tracking-tight">{stat.value}</span>
              <span className="text-white/40 text-sm font-medium uppercase tracking-widest">{stat.label}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default AboutSection;
